// Rule-based regular conjugation reference. Single source of truth for the
// "what would this verb look like if it were fully regular for its class" check
// used by both the Reference-tab highlighting and the drill pool logic.

export type EndingType = 'ar' | 'er' | 'ir'

// Suffixes by verb class, column, and pronoun index (eu, tu, você, nós, eles).
// `null` = the missing eu imperative. futureProgressive is compound so it has no
// entry. (vós is archaic and not modelled.)
export const REGULAR_ENDINGS: Record<EndingType, Partial<Record<string, Array<string | null>>>> = {
  ar: {
    present: ['o', 'as', 'a', 'amos', 'am'],
    preterite: ['ei', 'aste', 'ou', 'amos', 'aram'],
    imperfect: ['ava', 'avas', 'ava', 'ávamos', 'avam'],
    future: ['arei', 'arás', 'ará', 'aremos', 'arão'],
    conditional: ['aria', 'arias', 'aria', 'aríamos', 'ariam'],
    presentSubjunctive: ['e', 'es', 'e', 'emos', 'em'],
    imperfectSubjunctive: ['asse', 'asses', 'asse', 'ássemos', 'assem'],
    futureSubjunctive: ['ar', 'ares', 'ar', 'armos', 'arem'],
    imperativeAffirmative: [null, 'a', 'e', 'emos', 'em'],
    imperativeNegative: [null, 'es', 'e', 'emos', 'em'],
  },
  er: {
    present: ['o', 'es', 'e', 'emos', 'em'],
    preterite: ['i', 'este', 'eu', 'emos', 'eram'],
    imperfect: ['ia', 'ias', 'ia', 'íamos', 'iam'],
    future: ['erei', 'erás', 'erá', 'eremos', 'erão'],
    conditional: ['eria', 'erias', 'eria', 'eríamos', 'eriam'],
    presentSubjunctive: ['a', 'as', 'a', 'amos', 'am'],
    imperfectSubjunctive: ['esse', 'esses', 'esse', 'êssemos', 'essem'],
    futureSubjunctive: ['er', 'eres', 'er', 'ermos', 'erem'],
    imperativeAffirmative: [null, 'e', 'a', 'amos', 'am'],
    imperativeNegative: [null, 'as', 'a', 'amos', 'am'],
  },
  ir: {
    present: ['o', 'es', 'e', 'imos', 'em'],
    preterite: ['i', 'iste', 'iu', 'imos', 'iram'],
    imperfect: ['ia', 'ias', 'ia', 'íamos', 'iam'],
    future: ['irei', 'irás', 'irá', 'iremos', 'irão'],
    conditional: ['iria', 'irias', 'iria', 'iríamos', 'iriam'],
    presentSubjunctive: ['a', 'as', 'a', 'amos', 'am'],
    imperfectSubjunctive: ['isse', 'isses', 'isse', 'íssemos', 'issem'],
    futureSubjunctive: ['ir', 'ires', 'ir', 'irmos', 'irem'],
    imperativeAffirmative: [null, 'e', 'a', 'amos', 'am'],
    imperativeNegative: [null, 'as', 'a', 'amos', 'am'],
  },
}

export const PRONOUN_KEYS = ['eu', 'tu', 'você', 'nós', 'eles'] as const
export type PronounKey = (typeof PRONOUN_KEYS)[number]

export function endingTypeOf(infinitive: string): EndingType {
  if (infinitive.endsWith('ar')) return 'ar'
  if (infinitive.endsWith('er')) return 'er'
  return 'ir'
}

export function commonPrefixLength(a: string, b: string) {
  let index = 0
  while (index < a.length && index < b.length && a[index] === b[index]) index += 1
  return index
}

// Front-vowel spelling shift for -ar stems (jogar → joguei, dançar → dancei).
function orthoBeforeFrontVowel(stem: string) {
  if (stem.endsWith('c')) return `${stem.slice(0, -1)}qu`
  if (stem.endsWith('ç')) return `${stem.slice(0, -1)}c`
  if (stem.endsWith('g')) return `${stem}u`
  return stem
}

// The orthographically-correct fully-regular form of `infinitive` for one
// column/pronoun, or null when there is no such form (eu imperative).
export function expectedRegularForm(infinitive: string, column: string, pronounIndex: number): string | null {
  const type = endingTypeOf(infinitive)
  const ending = REGULAR_ENDINGS[type][column]?.[pronounIndex]
  if (ending === null || ending === undefined) return null
  let stem = infinitive.slice(0, -2)
  if (type === 'ar' && (column === 'presentSubjunctive' || (column === 'preterite' && pronounIndex === 0))) {
    stem = orthoBeforeFrontVowel(stem)
  }
  return stem + ending
}
